import React, { useState, useMemo } from 'react';
import { CenteredModal } from '~/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faKey, faLock, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import AuthAPI from '~/api/authApi';
import { Oval } from "react-loader-spinner";
import '~/pages/authentications/auth.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function ChangePasswordAdminModal({ show, onClose }) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        CurrentPassword: '',
        NewPassword: '',
        ConfirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [errors, setErrors] = useState({});
    
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
        if (message) {
            setMessage('');
        }
    };

    const requirements = useMemo(() => [
        { label: "10+ characters", met: formData.NewPassword.length >= 10 },
        { label: "Uppercase", met: /[A-Z]/.test(formData.NewPassword) },
        { label: "Lowercase", met: /[a-z]/.test(formData.NewPassword) },
        { label: "Number", met: /\d/.test(formData.NewPassword) },
        { label: "Special char", met: /[@$!%*?&]/.test(formData.NewPassword) },
    ], [formData.NewPassword]);

    const strength = useMemo(() => {
        const metCount = requirements.filter(r => r.met).length;
        if (metCount === 0) return { percent: 0, color: "#e5e7eb", text: "", textColor: "#9ca3af" };
        if (metCount <= 2) return { percent: 30, color: "#ef4444", text: "Weak", textColor: "#ef4444" };
        if (metCount <= 3) return { percent: 55, color: "#f59e0b", text: "Fair", textColor: "#f59e0b" };
        if (metCount <= 4) return { percent: 80, color: "#3b82f6", text: "Good", textColor: "#3b82f6" };
        return { percent: 100, color: "#10b981", text: "Strong", textColor: "#10b981" };
    }, [requirements]);

    const handleClose = () => {
        setFormData({ CurrentPassword: '', NewPassword: '', ConfirmPassword: '' });
        setErrors({});
        setMessage('');
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        if (onClose) onClose();
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setMessage('');
        setErrors({});

        const newErrors = {};
        
        if (!formData.CurrentPassword) {
            newErrors.CurrentPassword = 'Current Password is required.';
        }
        
        if (!formData.NewPassword) {
            newErrors.NewPassword = 'New Password is required.';
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{10,}$/.test(formData.NewPassword)) {
            newErrors.NewPassword = 'Password must have at least 10 characters, 1 lowercase, 1 uppercase, 1 number and 1 special character.';
        }
        
        if (!formData.ConfirmPassword) {
            newErrors.ConfirmPassword = 'Confirm Password is required.';
        } else if (formData.NewPassword !== formData.ConfirmPassword) {
            newErrors.ConfirmPassword = 'Passwords do not match.';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        
        // Use the same structure that AccountSecurityTab does
        const payload = {
            Password: formData.CurrentPassword,
            NewPassword: formData.NewPassword,
            RePassword: formData.ConfirmPassword,
            IsContinueSignIn: true
        };

        try {
            const res = await AuthAPI.changePassword(payload);
            if (res.data?.Succeeded || res.data?.succeeded) {
                setMessageType('success');
                setMessage('Password changed successfully! You will be logged out in 3 seconds...');
                setFormData({ CurrentPassword: '', NewPassword: '', ConfirmPassword: '' });
                setTimeout(() => {
                    handleClose();
                    axios.post(`${process.env.REACT_APP_API_URL}/api/auth/admin-sign-out`, {}, { withCredentials: true })
                         .then(() => { navigate("/auth/admin-signin", { replace: true }); })
                         .catch(() => { navigate("/auth/admin-signin", { replace: true }); });
                }, 3000);
            } else {
                setMessageType('danger');
                setMessage(res.data?.Message || res.data?.message || 'Failed to change password.');
            }
        } catch (error) {
            setMessageType('danger');
            setMessage(error.response?.data?.Message || error.response?.data?.message || error.message);
        }
        
        setLoading(false);
    };

    return (
        <CenteredModal
            show={show}
            onClose={handleClose}
            title="Change Password"
            confirmText="Update Password"
            size="md"
            modalType="modal-md"
            formId="formChangePassword"
            isSubmitting={loading}
            submittingText="Updating..."
            blurred={false}
        >
            <div className="px-3 pb-2 pt-2">
                <style>
                    {`#formChangePassword input.custom-pwd-input.form-control { padding-left: 40px !important; padding-right: 40px !important; }`}
                </style>
                <p className="text-muted fw-bold fs-9 mb-4">
                    Update your account password securely.
                </p>

                {message && (
                    <div className={`auth-${messageType === 'danger' ? 'error' : messageType}-message mb-4`}>
                        <FontAwesomeIcon icon={messageType === 'danger' ? faExclamationTriangle : faCheckCircle} className={messageType === 'danger' ? "text-danger" : "text-success"} />
                        <span>{message}</span>
                    </div>
                )}

                <form id="formChangePassword" onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                            Current Password <span className="text-danger">*</span>
                        </label>
                        <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                            Enter your current password to authorize changes.
                        </p>
                        <div className="position-relative">
                            <span className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary">
                                <FontAwesomeIcon icon={faLock} />
                            </span>
                            <input
                                type={showCurrentPassword ? "text" : "password"}
                                name="CurrentPassword"
                                className={`form-control custom-pwd-input ${errors.CurrentPassword ? 'is-invalid' : ''}`}
                                placeholder="Enter current password"
                                value={formData.CurrentPassword}
                                onChange={handleChange}
                                disabled={loading}
                                autoComplete="current-password"
                            />
                            <FontAwesomeIcon 
                                icon={showCurrentPassword ? faEyeSlash : faEye} 
                                className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary" 
                                style={{ cursor: 'pointer', zIndex: 10 }}
                                onClick={() => setShowCurrentPassword(!showCurrentPassword)} 
                            />
                        </div>
                        {errors.CurrentPassword && <div className="text-danger fw-bold fs-9 mt-1">{errors.CurrentPassword}</div>}
                    </div>

                    <div className="mb-4">
                        <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                            New Password <span className="text-danger">*</span>
                        </label>
                        <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                            Password must be at least 10 characters, with letters and numbers.
                        </p>
                        <div className="position-relative">
                            <span className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary">
                                <FontAwesomeIcon icon={faKey} />
                            </span>
                            <input
                                type={showNewPassword ? "text" : "password"}
                                name="NewPassword"
                                className={`form-control custom-pwd-input ${errors.NewPassword ? 'is-invalid' : ''}`}
                                placeholder="Enter new password"
                                value={formData.NewPassword}
                                onChange={handleChange}
                                disabled={loading}
                                autoComplete="new-password"
                            />
                            <FontAwesomeIcon 
                                icon={showNewPassword ? faEyeSlash : faEye} 
                                className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary" 
                                style={{ cursor: 'pointer', zIndex: 10 }}
                                onClick={() => setShowNewPassword(!showNewPassword)} 
                            />
                        </div>
                        {errors.NewPassword && <div className="text-danger fw-bold fs-9 mt-1">{errors.NewPassword}</div>}
                        
                        {formData.NewPassword.length > 0 && (
                            <div className="mt-2 px-1">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="fs-10 fw-bold text-body-tertiary">Password Strength</span>
                                    <span className="fs-10 fw-bold" style={{ color: strength.textColor }}>
                                        {strength.text}
                                    </span>
                                </div>
                                <div className="progress" style={{ height: "4px" }}>
                                    <div
                                        className="progress-bar"
                                        role="progressbar"
                                        style={{ width: `${strength.percent}%`, backgroundColor: strength.color, transition: 'width 0.3s ease, background-color 0.3s ease' }}
                                        aria-valuenow={strength.percent}
                                        aria-valuemin="0"
                                        aria-valuemax="100"
                                    ></div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mb-4">
                        <label className="d-block fw-bold mb-0" style={{ fontSize: '0.875rem', color: '#16191f' }}>
                            Confirm New Password <span className="text-danger">*</span>
                        </label>
                        <p style={{ fontSize: '0.78rem', color: '#687078', margin: '2px 0 6px 0' }}>
                            Re-enter the password to confirm.
                        </p>
                        <div className="position-relative">
                            <span className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary">
                                <FontAwesomeIcon icon={faKey} />
                            </span>
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="ConfirmPassword"
                                className={`form-control custom-pwd-input ${errors.ConfirmPassword ? 'is-invalid' : ''}`}
                                placeholder="Confirm new password"
                                value={formData.ConfirmPassword}
                                onChange={handleChange}
                                disabled={loading}
                                autoComplete="new-password"
                            />
                            <FontAwesomeIcon 
                                icon={showConfirmPassword ? faEyeSlash : faEye} 
                                className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary" 
                                style={{ cursor: 'pointer', zIndex: 10 }}
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)} 
                            />
                        </div>
                        {errors.ConfirmPassword && <div className="text-danger fw-bold fs-9 mt-1">{errors.ConfirmPassword}</div>}
                    </div>
                </form>
            </div>
        </CenteredModal>
    );
}

export default ChangePasswordAdminModal;
